import { useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { Logo } from "../../components/Logo";
import { api } from "../../lib/api";
import { useSession } from "../../lib/session";
import { personalApi, type PersonalApproval } from "./api";
import "./personal.css";

function PersonalShell({ title, children }: { title: string; children: ReactNode }) {
  const session = useSession();
  const cache = useQueryClient();
  const navigate = useNavigate();
  const context = useQuery({ queryKey: ["user-context"], queryFn: api.getUserContext });
  return <div className="personal-page">
    <header className="personal-header"><Logo /><nav aria-label="Personal account">
      <Link to="/personal-inbox">Inbox</Link>
      {context.data?.active_organization ? <Link to="/">Workspace</Link> : null}
      <button type="button" className="secondary-action" onClick={() => { session.clearSession(); cache.clear(); navigate("/login"); }}>Sign out</button>
    </nav></header>
    <main className="personal-main"><h1>{title}</h1>
      {context.data?.pending_organizations?.length ? <section className="personal-request">
        <h2>Organization awaiting review</h2>
        <p>{context.data.pending_organizations.map(org => org.organization_name).join(", ")}</p>
        <p>Coprint is reviewing your organization. Your personal Inbox remains available. Refresh to check approval status.</p>
      </section> : null}
      {children}</main>
  </div>;
}

function Approval({ item }: { item: PersonalApproval }) {
  const cache = useQueryClient();
  const [confirm, setConfirm] = useState(false);
  const approve = useMutation({ mutationFn: () => personalApi.approve(item.task_id),
    onSuccess: () => { setConfirm(false); void cache.invalidateQueries({ queryKey: ["personal-approvals"] }); } });
  return <article className="personal-request">
    <div><h2>{item.title}</h2><p>{item.project_name}</p></div>
    <p>{item.status === "COMPLETED" ? "Approved" : "Awaiting your approval"}</p>
    {item.report?.file_url ? <a className="secondary-action" href={item.report.file_url} target="_blank" rel="noreferrer">View report</a>
      : <p>The project team’s report will appear here when it is ready.</p>}
    {item.status !== "COMPLETED" ? <div className="personal-actions">
      {confirm ? <><span>Confirm approval of this {item.type === "RECCE_APPROVAL" ? "recce report" : "design"}?</span>
        <button type="button" className="primary-action" disabled={approve.isPending} onClick={() => approve.mutate()}>{approve.isPending ? "Approving…" : "Confirm approval"}</button>
        <button type="button" className="secondary-action" disabled={approve.isPending} onClick={() => setConfirm(false)}>Cancel</button></>
        : <button type="button" className="primary-action" onClick={() => setConfirm(true)}>Approve</button>}
    </div> : null}
    {approve.error ? <p role="alert" className="personal-error">{approve.error.message}</p> : null}
  </article>;
}

export function PersonalInboxPage() {
  const cache = useQueryClient();
  const [all, setAll] = useState(false);
  const query = useQuery({ queryKey: ["personal-approvals", all], queryFn: () => personalApi.approvals(all) });
  return <PersonalShell title="Inbox">
    <p>Recce and design approvals assigned to you. An organization is not required.</p>
    <div className="personal-actions"><button className={all ? "secondary-action" : "primary-action"} type="button" aria-pressed={!all} onClick={() => setAll(false)}>Pending</button>
      <button className={all ? "primary-action" : "secondary-action"} type="button" aria-pressed={all} onClick={() => setAll(true)}>All requests</button>
      <button className="secondary-action" type="button" disabled={query.isFetching} onClick={() => { void query.refetch(); void cache.invalidateQueries({ queryKey: ["user-context"] }); }}>{query.isFetching ? "Refreshing…" : "Refresh"}</button></div>
    {query.isPending ? <p role="status">Loading approvals…</p> : null}
    {query.error ? <p role="alert" className="personal-error">{query.error.message}</p> : null}
    {query.data?.length === 0 ? <section className="personal-request"><h2>No {all ? "approval requests" : "pending approvals"}</h2><p>Requests assigned to you will appear here.</p></section> : null}
    {query.data?.map(item => <Approval key={item.task_id} item={item} />)}
  </PersonalShell>;
}
